import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, SearchIcon, TextInput } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, PeriodDateRangeFields, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';
import { accountingReturnState } from '../shared/navigation';

type Row = {
  documentId: number;
  code: string | null;
  documentTypeCode: string;
  documentTypeName: string;
  documentDate: string | null;
  postingDate: string | null;
  description: string | null;
  lineCount: number;
  debitLocal: number;
  creditLocal: number;
  isBalanced: boolean;
  createdBy: string | null;
};

type Response = { rows: Row[]; totals: { recordCount: number } };

export const JournalRegister: React.FC = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const f = useAccountingReportFilters();
  const [search, setSearch] = useState('');

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/journal-register', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          companyIds: f.companyIdsParam || undefined,
          search: search || undefined,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const returnState = useMemo(
    () => accountingReturnState(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'code',
        header: 'Doc #',
        render: (v, row) =>
          v ? (
            <Link
              to={`/accounting/journals/${row.documentId}/edit`}
              state={returnState}
              className="text-indigo-600 hover:underline"
            >
              {v}
            </Link>
          ) : (
            `#${row.documentId}`
          ),
      },
      { key: 'documentTypeCode', header: 'Type' },
      { key: 'postingDate', header: 'Posting', render: (v) => (v ? formatDateGlobal(v) : '—') },
      { key: 'description', header: 'Description' },
      { key: 'lineCount', header: 'Lines', align: 'right' },
      { key: 'debitLocal', header: 'Debit', align: 'right', render: formatReportAmount },
      { key: 'creditLocal', header: 'Credit', align: 'right', render: formatReportAmount },
      { key: 'isBalanced', header: 'Balanced', render: (v: boolean) => (v ? 'Yes' : 'No') },
      { key: 'createdBy', header: 'Created by', render: (v) => v ?? '—' },
    ],
    [returnState],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <PeriodDateRangeFields
          periodId={f.periodId}
          periodOptions={f.periodOptions}
          onPeriodChange={(value) => {
            f.setPeriodId(value);
            f.setRun(false);
          }}
          dateFrom={f.dateFrom}
          dateTo={f.dateTo}
          onDateFromChange={(value) => {
            f.setPeriodId('');
            f.setDateFrom(value);
            f.setRun(false);
          }}
          onDateToChange={(value) => {
            f.setPeriodId('');
            f.setDateTo(value);
            f.setRun(false);
          }}
        />
        <div className="min-w-[12rem]">
          <FieldLabel>Search</FieldLabel>
          <TextInput value={search} onChange={(e) => { setSearch(e.target.value); f.setRun(false); }} placeholder="Code or description…" />
        </div>
      </div>
      <div className={cn('flex flex-wrap items-center gap-3 pt-3 border-t', colors.border)}>
        <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
          <span className={cn('h-1.5 w-1.5 rounded-full', f.allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
          {f.companyScopeLabel}
        </Badge>
        <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Journal Register"
      exportFileBase="journal-register"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      recordCount={data?.totals.recordCount ?? rows.length}
      readyMessage="Choose period and click Search to list journal headers."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => String(r.documentId)}
        showFooter
        emptyMessage="No journals in period."
        stickyHeader
      />
    </AccountingReportShell>
  );
};
